import React, { useEffect, useState } from "react";
import AlertPopup from "../../../AlertPopup";
import LoaderComp from "../../../LoaderComp";
import styles from "../LeadGenBotSettings/BotSettings.module.css";
import ExcludePathsStyle from "./ExcludePathsSettings.module.css";
import Modal from "../../../Modal";
import modalContentSty from "../LeadGenBotSettings/ModalContent.module.css";
import ExcludePathsManager from "./ExcludePathsManager";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
const getToken = () => localStorage.getItem("token");

// Add the new keys to CORE_KEYS so they get included in updates
const CORE_KEYS = [
  "app_name",
  "ai_agent_bot_enabled",
  "allow_nested",
  "app_id",
  "auth_key",
  "chat_settings",
  "enabled_bot_content",
  "feature",
  "region",
  "role",
  "settings",
  "tenant_id",
  "timestamp",
  "tenant_info",
  "isTraining",
  "session_enabled",
  "live_agent_enabled",
  "exclude_paths",        // Added
  "dont_show_chat_box_at_logout" // Added
];

export default function ExcludePathsSettings({ appData, tenantPlan }) {
  const [initialData, setInitialData] = useState(null);
  const [botContent, setBotContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayloads, setPendingUpdatePayloads] = useState({ payload1: null });

  const hasChanges = () => {
    if (!initialData || !botContent) return false;

    // Check if any of the CORE_KEYS have changed
    const coreChanged = CORE_KEYS.some(key => {
      // Skip chat_settings as it's handled separately in the original
      if (key === 'chat_settings') return false;
      
      // Handle cases where properties might be undefined
      if (initialData.botContent[key] === undefined || botContent[key] === undefined) {
        return false;
      }
      
      return JSON.stringify(initialData.botContent[key]) !== JSON.stringify(botContent[key]);
    });

    return coreChanged;
  };

  const confirmUpdate = async (confirmed) => {
    setShowConfirmModal(false);

    if (!confirmed) {
      showAlert("info", "Update cancelled.");
      return;
    }

    const { payload1 } = pendingUpdatePayloads;

    if (!payload1) {
      showAlert("error", "Update data not found");
      return;
    }

    setUpdating(true);

    try {
      const token = getToken();

      const region_prefix = appData.region.toLowerCase();
      const updateAppResponse = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/update_app?act_type=tenant&app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload1),
        }
      );

      const updateAppData = await updateAppResponse.json();

      if (!updateAppResponse.ok) {
        showAlert("error", "Failed to update Exclude Paths configuration");
        setUpdating(false);
        return;
      }

      setInitialData({
        botContent: { ...botContent }
      });

      showAlert("success", "Exclude Paths settings updated successfully!");

      if (window.MyPackage?.processHardReset) {
        window.MyPackage.processHardReset(token, appData.app_name, appData.region.toLowerCase());
      }

    } catch (error) {
      console.error("Update failed:", error);
      showAlert("error", "Update failed: " + error.message);
    } finally {
      setUpdating(false);
      setPendingUpdatePayloads({ payload1: null });
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  useEffect(() => {
    if (!appData?.tenant_id || !appData?.app_name) return;

    const fetchBotContent = async () => {
      setLoading(true);

      try {
      const region = appData.region.toLowerCase()
        const url = `https://${region}.autobot2.${backend_api_base_url}/get_bot_content?app_name=${appData.app_name}&tenant_id=${appData.tenant_id}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "x-api-key": "bGVnYWwxMjNfX1NFVFJBVE9SX190ZXN0ZGRk",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch bot content");

        const data = await response.json();
        const fetchedBotContent = {
          ...data?.bot_payload,
          ...appData
        };

        console.log("Fetched bot content for Exclude Paths:", fetchedBotContent);
        setBotContent(fetchedBotContent);
        setInitialData({
          botContent: { ...fetchedBotContent }
        });
      } catch (err) {
        showAlert("error", "❌ " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBotContent();
  }, [appData]);

  const handleUpdate = async () => {
    if (!botContent) {
      showAlert("error", "No content to update");
      return;
    }

    const payload1 = {};
    Object.keys(botContent).forEach((key) => {
      if (CORE_KEYS.includes(key)) payload1[key] = botContent[key];
    });

    console.log("Exclude Paths Payload:", payload1);
    setPendingUpdatePayloads({ payload1 });
    setShowConfirmModal(true);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const handleModalClose = () => {
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className={ExcludePathsStyle.centered}>
        <LoaderComp message="Loading Exclude Paths Settings" />
      </div>
    );
  }

  if (!botContent) {
    return <div>Error: Unable to load Exclude Paths content</div>;
  }

  return (
    <div className={ExcludePathsStyle.botContent_wrap}>
      <h2 className={ExcludePathsStyle.title}>Path & Visibility Settings</h2>

      {/* Child component that handles both settings */}
      <ExcludePathsManager
        setBotContent={setBotContent}
        botContent={botContent}
      />

      <button
        onClick={handleUpdate}
        disabled={updating || !botContent || loading || !hasChanges()}
        className={`${ExcludePathsStyle.updateBtn} ${updating ? ExcludePathsStyle.disabled : ''}`}
      >
        {updating ? "Updating..." : "Update Path Settings"}
      </button>

      {alert.show && (
        <AlertPopup
          type={alert.type}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      )}

      <Modal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        hideCloseButton={true}
        title="Confirmation Required"
      >
        <div className={modalContentSty.modalContent}>
          <div className={modalContentSty.modalContentWrap}>
            <div className={modalContentSty.modalContentIc}>⚠️</div>
            <p className={modalContentSty.modalMessage}>
              All the clients would be rebooted! Do you want to continue?
            </p>
          </div>
          <div className={modalContentSty.modalActions}>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
              onClick={() => confirmUpdate(false)}
            >
              Cancel
            </button>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
              onClick={() => confirmUpdate(true)}
            >
              Ok
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}